<template lang="pug">
form.section(v-on:submit.prevent="submit")
  .field
    .control
      b-datepicker(icon="calendar" placeholder="Click to select date" ref="focus" v-model="$v.bookingStartDate.$model")
  .field
    .label From:
  .field(v-if="!startHour.disabled" v-for="startHour in startHours")
    .control
      b-radio(v-bind:disabled="startHour.disabled" v-bind:native-value="startHour.value" v-model="$v.bookingStartHour.$model") {{ startHour.label }}
  .field(v-if="noAvailableStartHours")
    .control
      .icon
        i.far.fa-frown
      span Nothing available.
  template(v-if="bookingStartHour")
    .field
      .label To:
    .field(v-if="!endHour.disabled" v-for="endHour in endHours")
      .control
        b-radio(v-bind:disabled="endHour.disabled" v-bind:native-value="endHour.value" v-model="$v.bookingEndHour.$model") {{ endHour.label }}
    .field(v-if="noAvailableEndHours")
      .control
        .icon
          i.far.fa-frown
        span Nothing available.
    template(v-if="bookingEndHour")
      .field
        .control
          b-input(placeholder="Optional Note" type="textarea" v-model="$v.bookingNote.$model")
  .field
    .control
      button.button.is-fullwidth.is-primary(type="submit" v-bind:disabled="$v.$invalid") Place External Booking
</template>

<script>
import { addMinutes, areRangesOverlapping, format, isAfter, isBefore, isWithinRange, parse, startOfDay } from 'date-fns'
import { required } from 'vuelidate/lib/validators'

import Constants from '@/constants'

import ApiMixin from '@/mixins/ApiMixin.vue'
import DeviceMixin from '@/mixins/DeviceMixin.vue'

const CREATE_BOOKING_DURATION_MINIMUM = 15
const CREATE_BOOKING_DURATION_SLOTS = 24 * 60 / CREATE_BOOKING_DURATION_MINIMUM

export default {
  mixins: [
    ApiMixin,
    DeviceMixin
  ],
  props: {
    initialBookingStartDate: {
      default: null,
      required: false,
      type: Date
    }
  },
  data () {
    return {
      bookingEndHour: null,
      bookingNote: null,
      bookingStartDate: null,
      bookingStartHour: null
    }
  },
  computed: {
    bookingEnd () {
      return addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * this.bookingEndHour)
    },
    bookingStart () {
      return addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * this.bookingStartHour)
    },
    endHours () {
      return Array(CREATE_BOOKING_DURATION_SLOTS).fill({}).map((_, index) => {
        const bookingEnd = addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * index)
        const bookingStart = addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * this.bookingStartHour)
        const conflictedProfessionalBooking = this.apiProfessionalBookings.findIndex((booking) => {
          if (booking.booking_status === Constants.STATUS_ACCEPTED) {
            if (isBefore(bookingStart, bookingEnd)) {
              return areRangesOverlapping(bookingStart, bookingEnd, this.parsedDateTime(booking.booking_start), this.parsedDateTime(booking.booking_end)) || (isBefore(bookingStart, this.parsedDateTime(booking.booking_start)) && isAfter(bookingEnd, this.parsedDateTime(booking.booking_start)))
            }
          }
        }) > -1
        const conflictedSlot = this.apiProfessionalSlots.findIndex((slot) => {
          return isWithinRange(bookingStart, this.parsedDateTime(slot.start_time), this.parsedDateTime(slot.end_time)) && isWithinRange(bookingEnd, this.parsedDateTime(slot.start_time), this.parsedDateTime(slot.end_time))
        }) === -1
        const conflictedStart = index <= this.bookingStartHour
        return {
          disabled: conflictedProfessionalBooking || conflictedSlot || conflictedStart,
          label: this.formattedDateTime(bookingEnd).substring(11, 16),
          value: index
        }
      })
    },
    startHours () {
      const bookingStartMin = new Date()
      return Array(CREATE_BOOKING_DURATION_SLOTS).fill({}).map((_, index) => {
        const bookingEnd = addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * (index + 1))
        const bookingStart = addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * (index + 0))
        const conflictedProfessionalBooking = this.apiProfessionalBookings.findIndex((booking) => {
          if (booking.booking_status === Constants.STATUS_ACCEPTED) {
            return areRangesOverlapping(bookingStart, bookingEnd, this.parsedDateTime(booking.booking_start), this.parsedDateTime(booking.booking_end))
          }
        }) > -1
        const conflictedSlot = this.apiProfessionalSlots.findIndex((slot) => {
          return isWithinRange(bookingStart, this.parsedDateTime(slot.start_time), this.parsedDateTime(slot.end_time)) && isWithinRange(bookingEnd, this.parsedDateTime(slot.start_time), this.parsedDateTime(slot.end_time))
        }) === -1
        const conflictedStart = bookingStart < bookingStartMin
        return {
          disabled: conflictedProfessionalBooking || conflictedSlot || conflictedStart,
          label: this.formattedDateTime(bookingStart).substring(11, 16),
          value: index
        }
      })
    },
    noAvailableEndHours () {
      return this.endHours.filter((endHour) => {
        return !endHour.disabled
      }).length === 0
    },
    noAvailableStartHours () {
      return this.startHours.filter((startHour) => {
        return !startHour.disabled
      }).length === 0
    }
  },
  watch: {
    async apiMixinInitialised () {
      await this.initialiseAddProfessionalBooking()
    },
    bookingEndHour () {
      this.initialiseBookingNote()
    },
    bookingStartDate () {
      this.initialiseBookingStartHour()
    },
    bookingStartHour () {
      this.initialiseBookingEndHour()
    }
  },
  beforeDestroy () {
    this.$root.$off('initialiseAddBooking')
  },
  mounted () {
    this.$root.$on('initialiseAddBooking', () => {
      this.initialiseAddProfessionalBooking()
    })
    if (this.initialBookingStartDate) {
      this.initialiseAddProfessionalBooking()
    }
  },
  methods: {
    formattedDateTime (dateTime) {
      return format(dateTime, 'YYYY-MM-DD HH:mm:ss')
    },
    initialiseAddProfessionalBooking () {
      this.deviceSetFocus()
      this.initialiseBookingNote()
      this.initialiseBookingStartDate()
      this.initialiseBookingStartHour()
      this.initialiseValidation()
    },
    initialiseBookingEndHour () {
      this.bookingEndHour = null
    },
    initialiseBookingNote () {
      this.bookingNote = null
    },
    initialiseBookingStartDate () {
      this.bookingStartDate = this.initialBookingStartDate || startOfDay(new Date())
    },
    initialiseBookingStartHour () {
      this.bookingStartHour = null
    },
    initialiseValidation () {
      this.$v.$reset()
    },
    parsedDateTime (dateTime) {
      return parse(dateTime, 'YYYY-MM-DD HH:mm:ss')
    },
    async submit () {
      await this.apiCreateBooking(this.formattedDateTime(this.bookingEnd), this.formattedDateTime(this.bookingStart), 1, null, this.apiUserId, this.bookingNote)
    }
  },
  validations () {
    return {
      bookingEndHour: {
        required
      },
      bookingNote: {},
      bookingStartDate: {
        required
      },
      bookingStartHour: {
        required
      }
    }
  }
}
</script>
